import { call, put, select } from 'redux-saga/effects';
import * as types from './chart.types';
import * as actions from './chart.actions';
import { chartSelectors } from './chart.selectors';
import { apiRequest } from '../../api/api.saga-services';
import * as api from '../../api/request-config/chart.api';
import * as nodeResourceService from '../node/node.saga-service';
import { IOrganizationChartNode } from '../node/node.types';

export interface IChartWithNodesResult {
  chart: types.IChart | null;
  nodes: IOrganizationChartNode[] | null;
}

function parsePublished(value: unknown): boolean {
  return value === true || value === 1 || value === '1';
}

export function normalizeChart(chart: Record<string, unknown>): types.IChart {
  return {
    id: String(chart.id),
    tenantId: String(chart.tenantId ?? ''),
    type: String(chart.type ?? ''),
    status: chart.status as types.IChart['status'],
    published: parsePublished(chart.published),
    createdAt: chart.createdAt as string | undefined,
    updatedAt: chart.updatedAt as string | undefined,
    lastPublishedAt: chart.lastPublishedAt as string | undefined,
    changedBy: chart.changedBy !== undefined && chart.changedBy !== null ? String(chart.changedBy) : undefined,
  };
}

function* storeChartWithNodes(
  result: Record<string, unknown> | null,
): Generator<unknown, IChartWithNodesResult, unknown> {
  if (!result?.id) {
    return { chart: null, nodes: null };
  }

  const { nodes, ...chartData } = result;
  const chart = normalizeChart(chartData as Record<string, unknown>);
  yield call(storeChart, chart);

  const nodeList = (nodes as IOrganizationChartNode[] | undefined) ?? null;
  let normalizedNodes: IOrganizationChartNode[] | null = null;
  if (nodeList?.length) {
    const stored = (yield call(nodeResourceService.storeNode, nodeList)) as
      | IOrganizationChartNode
      | IOrganizationChartNode[];
    normalizedNodes = Array.isArray(stored) ? stored : [stored];
  }

  return { chart, nodes: normalizedNodes };
}

export function* getChart(
  chartType: string,
  options?: api.GetChartOptions,
): Generator<unknown, IChartWithNodesResult, unknown> {
  const result = (yield call(apiRequest, api.getChartApi(chartType, options))) as Record<string, unknown> | null;
  return (yield call(storeChartWithNodes, result)) as IChartWithNodesResult;
}

export function* listChartVersions(chartType: string): Generator<unknown, types.IChart[], unknown> {
  const list = (yield call(apiRequest, api.listChartVersionsApi(chartType))) as Record<string, unknown>[] | null;
  if (!list?.length) {
    return [];
  }

  return list.map((item) => normalizeChart(item));
}

export function* publishChartVersion(
  chartType: string,
  chartId: types.IChart['id'],
): Generator<unknown, IChartWithNodesResult | null, unknown> {
  const result = (yield call(apiRequest, api.publishChartVersionApi(chartType, chartId))) as Record<
    string,
    unknown
  > | null;
  if (!result) {
    return null;
  }

  return (yield call(storeChartWithNodes, result)) as IChartWithNodesResult;
}

export function* publishDraftChart(chartType: string): Generator<unknown, IChartWithNodesResult | null, unknown> {
  const result = (yield call(apiRequest, api.publishDraftChartApi(chartType))) as Record<string, unknown> | null;
  if (!result) {
    return null;
  }

  return (yield call(storeChartWithNodes, result)) as IChartWithNodesResult;
}

export function* createEmptyDraft(chartType: string): Generator<unknown, IChartWithNodesResult | null, unknown> {
  const result = (yield call(apiRequest, api.createEmptyDraftApi(chartType))) as Record<string, unknown> | null;
  if (!result) {
    return null;
  }

  return (yield call(storeChartWithNodes, result)) as IChartWithNodesResult;
}

export function* createDraftFromVersion(
  chartType: string,
  sourceChartId: types.IChart['id'],
): Generator<unknown, IChartWithNodesResult | null, unknown> {
  const result = (yield call(apiRequest, api.createDraftFromVersionApi(chartType, sourceChartId))) as Record<
    string,
    unknown
  > | null;
  if (!result) {
    return null;
  }

  return (yield call(storeChartWithNodes, result)) as IChartWithNodesResult;
}

export function* deleteChart(chartType: string, chartId: types.IChart['id']): Generator<unknown, boolean, unknown> {
  const result = (yield call(apiRequest, api.deleteChartApi(chartType, chartId))) as unknown;
  return result !== null;
}

export function* storeChart(chart: types.IChart) {
  yield put(actions.storeChart(chart));
  return chart;
}

export function* mergeChartMetadata(chart: types.IChart): Generator<unknown, types.IChart, unknown> {
  const existing = (yield select(chartSelectors.selectResourceById, chart.id)) as types.IChart | null;

  if (existing) {
    const merged: types.IChart = {
      ...existing,
      changedBy: chart.changedBy ?? existing.changedBy,
      updatedAt: chart.updatedAt ?? existing.updatedAt,
    };
    yield call(storeChart, merged);
    return merged;
  }

  const normalized = normalizeChart((chart as unknown) as Record<string, unknown>);
  yield call(storeChart, normalized);
  return normalized;
}

export function* clearChart(chartId: types.IChart['id']) {
  yield put(actions.clearChart(chartId));
}

export function* clearAllCharts() {
  const chartIds: types.IChart['id'][] = yield select(chartSelectors.selectResourceIds);
  yield put(actions.clearChart(chartIds));
}
